import { useEffect } from 'react';
import { Routes, Route, Navigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

// Accent palette injection (--accent-* CSS variables) lives in
// components/AccentInjector.tsx — single source of truth. It reads
// me.discordProfile.accentColor and the active org's accentColor, applies
// priority (user > org > forge), and themes the whole app.
import { Layout } from './components/Layout';
import { CommandCenter } from './pages/CommandCenter';
import { Standings } from './pages/Standings';
import { Bracket } from './pages/Bracket';
import { Matches } from './pages/Matches';
import { ClanDetail } from './pages/ClanDetail';
import { LeaderPanel } from './pages/LeaderPanel';
import { AdminPanel } from './pages/AdminPanel';
import { FounderAdmin } from './pages/FounderAdmin';
import { AdminTiers } from './pages/AdminTiers';
import { Expired } from './pages/Expired';
import { SeedingStandings } from './pages/SeedingStandings';
import { Divisions } from './pages/Divisions';
import { UserLookup } from './pages/UserLookup';
import { OnboardClient } from './pages/OnboardClient';
import { ActivityLog } from './pages/ActivityLog';
import { Observability } from './pages/Observability';
import { DisciplinePage } from './pages/DisciplinePage';
import { TraceView } from './pages/TraceView';
import { ClickstreamPanel } from './pages/ClickstreamPanel';
import { UserAnalytics } from './pages/UserAnalytics';
import { Organizations } from './pages/Organizations';
import { OrgCredits } from './pages/OrgCredits';
import { PublicMatchCard } from './pages/PublicMatchCard';
import { PlayerCard } from './pages/PlayerCard';
import { MyCard } from './pages/MyCard';
import { MyOrg } from './pages/MyOrg';
import { AssetManager } from './pages/AssetManager';
import { OrgBranding } from './pages/OrgBranding';
import { HowItWorks } from './pages/HowItWorks';
import { Privacy } from './pages/Privacy';
import { Terms } from './pages/Terms';
import { TournamentSetup } from './pages/TournamentSetup';
import { ClanManagement } from './pages/ClanManagement';
import { AdminMatchCreate } from './pages/AdminMatchCreate';
import { BrowseClans } from './pages/BrowseClans';
import { AlertsInbox } from './pages/AlertsInbox';
import { OrgCreationRequestsInbox } from './pages/OrgCreationRequestsInbox';
import { ClaimPage } from './pages/ClaimPage';
import { ClaimSeatPage } from './pages/ClaimSeatPage';
import { Handshake } from './pages/Handshake';
import { GateScreen } from './pages/GateScreen';
import { BuildClanScreen } from './pages/BuildClanScreen';
import { JoinCompScreen } from './pages/JoinCompScreen';
import { Watch } from './pages/Watch';
import { CompHome } from './pages/CompHome';
import { OrgHome } from './pages/OrgHome';
import { ClanHome } from './pages/ClanHome';
import { CompRules } from './pages/CompRules';
import { CompAdminApplications } from './pages/CompAdminApplications';
import { MyComp } from './pages/MyComp';
import { MyCompDetail } from './pages/MyCompDetail';
import { MatchCardLive } from './pages/MatchCardLive';
import { PersonalUpload } from './pages/PersonalUpload';
import { VetoPage } from './pages/VetoPage';
import { Calendar } from './pages/Calendar';
import { MatchCardDemo } from './pages/MatchCardDemo';
import { api } from './lib/api';
import { PageSpinner } from './components/Spinner';
import { ConsentGate } from './components/ConsentGate';
import { AmbientEffects } from './components/AmbientEffects';
import { ErrorBoundary } from './components/ErrorBoundary';
import { initClickstream } from './lib/clickstream';

// Old /match/:id and /submit/:id links forward to the v2 living match card,
// preserving the compId query string so polling resumes seamlessly.
function RedirectToMatchCard() {
  const { matchId } = useParams();
  const { search } = useLocation();
  if (!matchId) return <Navigate to="/" replace />;
  return <Navigate to={`/matchcard/${encodeURIComponent(matchId)}${search}`} replace />;
}

// ── Auth gate ─────────────────────────────────────────────────────────────────

function AuthGate({ children }: { children: React.ReactNode }) {
  const { data: me, isLoading: meLoading, error: meError } = useQuery({
    queryKey: ['me'],
    queryFn: api.me,
    retry: false,
  });

  if (meLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <PageSpinner />
      </div>
    );
  }

  if (meError) {
    const msg = (meError as Error).message || '';
    if (msg.includes('401') || msg.toLowerCase().includes('unauthorized')) {
      return <Expired />;
    }
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 text-center">
        <p className="text-red-400">Failed to connect to portal API.</p>
        <p className="font-mono text-sm text-iron-500">{msg}</p>
      </div>
    );
  }

  // POPIA consent gate — must accept before using the portal
  if (me && !me.popiaConsent) {
    return <ConsentGate />;
  }

  return <>{children}</>;
}

// GATE-flow routes (full-screen, no Layout shell). Auth-gated in production;
// a DEV-only ?as=<persona> preview bypasses the gate so the screens can be
// eyeballed across roles without a live session.
function GateRoute({ children }: { children: React.ReactNode }) {
  const [params] = useSearchParams();
  const devPreview = import.meta.env.DEV && Boolean(params.get('as'));
  if (devPreview) return <>{children}</>;
  return <AuthGate>{children}</AuthGate>;
}

// ── App ───────────────────────────────────────────────────────────────────────

export default function App() {
  // Clickstream capture (Lisa, 2026-07-19) — one delegated listener for the
  // whole app, wired ONCE here at the root (same "mount once above the whole
  // <Routes> tree" placement as AmbientEffects below). initClickstream() is
  // its own idempotent singleton — safe under StrictMode's double-invoked
  // effects — so this fires before any auth/consent gate resolves, covering
  // public/pre-auth pages (landing, /gate, /watch/:compId, …) too. See
  // lib/clickstream.ts's header for the full capture/POPIA/transport design.
  useEffect(() => {
    initClickstream();
  }, []);

  // (Previously bypassed AuthGate for /card/:matchId anonymous share links.
  // The living Match Card queries authenticated endpoints, so we keep it
  // inside AuthGate. A truly-anonymous public surface can be reintroduced
  // as a separate read-only endpoint later.)

  // GG Anvil ember/spark ambient brand effects, app-wide (Lisa's call,
  // 2026-07-03 — originally scoped to just the public viewing pages, then
  // widened to every page). Mounted ONCE here, above the whole <Routes>
  // tree, rather than per-route: since this wraps the entire router output,
  // React Router swapping matched routes underneath it doesn't remount or
  // unmount this wrapper, so the effect just runs for the whole app
  // session — no per-navigation mount/unmount lifecycle needed anymore.
  return (
    <AmbientEffects>
      <Routes>
        {/* Public, no AuthGate — claim links sign the user in mid-flight. */}
        <Route path="/claim/:token" element={<ClaimPage />} />
        {/* Public — open shareable seat link; the opener signs in, then claims. */}
        <Route path="/claim-seat/:token" element={<ClaimSeatPage />} />
        {/* Public — handshake page swaps a one-use blob for a session cookie,
            scrubs URL, then redirects to returnTo. */}
        <Route path="/handshake" element={<Handshake />} />
        {/* Public — read-only spectator page (opt-in per comp). No AuthGate:
            anyone with the link watches the bracket/standings live. */}
        <Route path="/watch/:compId" element={<Watch />} />
        {/* Competition home page: rules / org / cost / format, seen BEFORE
            joining. No frontend AuthGate wrapper, but NOT public — the backend
            GET /public/comp/:id independently gates it: 401 if unauthenticated,
            403 if not a member of the org's Discord server. (Do not assume the
            guild check is optional/frontend-only when adding sibling routes.) */}
        <Route path="/comp/:compId" element={<CompHome />} />
        {/* Public — shareable rules page (replaces the .docx download). */}
        <Route path="/comp/:compId/rules" element={<CompRules />} />
        {/* Public — org profile page (world-readable; only published pages resolve). */}
        <Route path="/org/:slug" element={<OrgHome />} />
        {/* Public — org-scoped clan profile page (no AuthGate; the fetch itself is
            gated server-side on org-visibility, not a live Discord check). */}
        <Route path="/team/:name" element={<ClanHome />} />
        {/* GATE — the role×time entry + its flow screens. Auth-gated (DEV ?as= bypasses). */}
        <Route path="/gate" element={<GateRoute><GateScreen /></GateRoute>} />
        <Route path="/gate/build-clan" element={<GateRoute><BuildClanScreen /></GateRoute>} />
        <Route path="/gate/join-comp" element={<GateRoute><JoinCompScreen /></GateRoute>} />
        {/* Everything else lives behind the auth gate. */}
        <Route path="*" element={
          <AuthGate>
            <Layout>
              <AuthedRoutes />
            </Layout>
          </AuthGate>
        } />
      </Routes>
    </AmbientEffects>
  );
}

function AuthedRoutes() {
  return (
    <Routes>
          <Route path="/" element={<CommandCenter />} />
          <Route path="/card/:matchId" element={<PublicMatchCard />} />
          <Route path="/standings" element={<Standings />} />
          <Route path="/bracket" element={<Bracket />} />
          <Route path="/matches" element={<Matches />} />
          {/* /match/:id and /submit/:id are legacy routes — both now redirect to
              the v2 living match card. Old links keep working. */}
          <Route path="/match/:matchId" element={<RedirectToMatchCard />} />
          <Route path="/submit/:matchId" element={<RedirectToMatchCard />} />
          <Route path="/matchcard/:matchId" element={<ErrorBoundary label="this match card"><MatchCardLive /></ErrorBoundary>} />
          {/* T3 (personal-upload-squad-select) — mobile-first, identity-scoped
              upload view: a player's own slots only, one round per row. The
              calendar's tile link (Calendar.tsx) points here; the page itself
              links back out to the full match card above for anyone who needs
              veto/roster/comments/forge. */}
          <Route path="/matchcard/:matchId/upload" element={<ErrorBoundary label="your upload"><PersonalUpload /></ErrorBoundary>} />
          <Route path="/veto/:matchId" element={<ErrorBoundary label="the veto"><VetoPage /></ErrorBoundary>} />
          <Route path="/calendar" element={<Calendar />} />
          <Route path="/admin/match-card-demo" element={<ErrorBoundary label="the OCR Lab"><MatchCardDemo /></ErrorBoundary>} />
          {/* Legacy /admin/ocr-test redirects to the v2 lab */}
          <Route path="/admin/ocr-test" element={<Navigate to="/admin/match-card-demo" replace />} />
          <Route path="/clan/:name" element={<ClanDetail />} />
          <Route path="/clans/browse" element={<BrowseClans />} />
          <Route path="/leader" element={<LeaderPanel />} />
          <Route path="/admin" element={<AdminPanel />} />
          <Route path="/admin/founder" element={<FounderAdmin />} />
          <Route path="/admin/tiers" element={<AdminTiers />} />
          <Route path="/admin/setup" element={<TournamentSetup />} />
          <Route path="/admin/clans" element={<ClanManagement />} />
          <Route path="/admin/matches/new" element={<AdminMatchCreate />} />
          <Route path="/admin/user-lookup" element={<UserLookup />} />
          <Route path="/admin/onboard" element={<OnboardClient />} />
          <Route path="/admin/comp-admin-applications" element={<CompAdminApplications />} />
          <Route path="/admin/activity" element={<ActivityLog />} />
          <Route path="/admin/observability" element={<Observability />} />
          <Route path="/admin/discipline" element={<DisciplinePage />} />
          <Route path="/admin/trace-view" element={<TraceView />} />
          <Route path="/admin/clickstream" element={<ClickstreamPanel />} />
          <Route path="/admin/analytics" element={<UserAnalytics />} />
          <Route path="/admin/assets" element={<AssetManager />} />
          <Route path="/admin/alerts" element={<AlertsInbox />} />
          <Route path="/admin/org-creation-requests" element={<OrgCreationRequestsInbox />} />
          <Route path="/orgs/:orgId/branding" element={<OrgBranding />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/seeding" element={<SeedingStandings />} />
          <Route path="/divisions" element={<Divisions />} />
          <Route path="/orgs" element={<Organizations />} />
          <Route path="/orgs/:orgId/credits" element={<OrgCredits />} />
          {/* In-shell org profile — same OrgHome/OrgPublicPageView content as the
              public /org/:slug page, but rendered inside the Layout so it swaps
              into the content area beside the sidebar instead of taking over the
              whole viewport. The sidebar "Org Page" link points here; the public
              /org/:slug route stays for external/shareable links. */}
          <Route path="/org-page/:slug" element={<OrgHome />} />
          {/* In-shell clan profile — same ClanHome/ClanPublicPageView content as the
              public /team/:name page, rendered inside the Layout instead of taking
              over the whole viewport. The public /team/:name route stays for
              external/shareable links. */}
          <Route path="/clan-page/:name" element={<ClanHome />} />
          <Route path="/me/card" element={<MyCard />} />
          <Route path="/me/org" element={<MyOrg />} />
          <Route path="/me/comp" element={<MyComp />} />
          <Route path="/me/comp/:compId" element={<MyCompDetail />} />
          <Route path="/player/:discordId" element={<PlayerCard />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/expired" element={<Expired />} />
          <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
